/**
 * Taxonomy category editor modal — create or edit a system tag category.
 *
 * Surface for the category CRUD endpoints in src/admin/taxonomy.js
 * (POST/PATCH/DELETE /api/admin/taxonomy/categories). Delete is rejected
 * server-side (409) when the category still contains tags.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-24-v1
 */
import { useEffect, useState } from 'react';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import {
    createTaxonomyCategory,
    deleteTaxonomyCategory,
    updateTaxonomyCategory
} from '../../lib/api';
import { getHttpErrorMessage } from '../../lib/http';
import type { TaxonomyCategory } from '../../lib/types';

// Must match COLOR_HEX_BY_VAR in src/admin/taxonomy.js
const COLOR_PALETTE: { var: string; hex: string; label: string }[] = [
    { var: '--primary', hex: '#3B82F6', label: 'Blue' },
    { var: '--purple', hex: '#A855F7', label: 'Purple' },
    { var: '--cyan', hex: '#06B6D4', label: 'Cyan' },
    { var: '--success', hex: '#22C55E', label: 'Green' },
    { var: '--warning', hex: '#F59E0B', label: 'Amber' },
    { var: '--danger', hex: '#EF4444', label: 'Red' },
    { var: '--muted', hex: '#94A3B8', label: 'Grey' },
    { var: '--text-muted', hex: '#64748B', label: 'Slate' },
    { var: '--text-dim', hex: '#475569', label: 'Dim slate' }
];

export interface TaxonomyCategoryEditorProps {
    open: boolean;
    mode: 'create' | 'edit';
    category?: TaxonomyCategory | null;
    onClose: () => void;
    onSaved: () => void;
}

export function TaxonomyCategoryEditorModal({
    open,
    mode,
    category,
    onClose,
    onSaved
}: TaxonomyCategoryEditorProps) {
    const [name, setName] = useState('');
    const [colorVar, setColorVar] = useState<string>('--primary');
    const [sortOrder, setSortOrder] = useState<number>(999);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setConfirmDelete(false);
        if (mode === 'edit' && category) {
            setName(category.name);
            setColorVar(category.color_var || '--primary');
            setSortOrder(category.sort_order ?? 999);
        } else {
            setName('');
            setColorVar('--primary');
            setSortOrder(999);
        }
    }, [open, mode, category]);

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Category name is required');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            if (mode === 'create') {
                await createTaxonomyCategory({
                    name: trimmed,
                    color_var: colorVar,
                    sort_order: sortOrder,
                    is_system: true
                });
            } else if (category) {
                await updateTaxonomyCategory(category.id, {
                    name: trimmed,
                    color_var: colorVar,
                    sort_order: sortOrder
                });
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(getHttpErrorMessage(err, 'Failed to save category'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!category) return;
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await deleteTaxonomyCategory(category.id);
            onSaved();
            onClose();
        } catch (err) {
            // 409 = still contains tags
            setError(getHttpErrorMessage(err, 'Failed to delete category'));
            setSubmitting(false);
        }
    };

    const title =
        mode === 'create' ? 'Add tag category' : `Edit category · ${category?.name ?? ''}`;
    const subtitle =
        mode === 'create'
            ? 'Creates a new default tag category (e.g. Tempo, Energy, Use Case). System categories are visible to every user.'
            : 'Updates this system tag category. Deleting is only allowed when the category has no tags.';

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            width={520}
            footer={
                <>
                    {mode === 'edit' ? (
                        <RoleButton
                            scope="owner_only"
                            className={`btn ${confirmDelete ? 'btn-danger' : 'btn-ghost'}`}
                            onClick={handleDelete}
                            disabled={submitting}
                            title={
                                confirmDelete
                                    ? 'Click again to confirm permanent deletion'
                                    : 'Retire this category (rejected if it still contains tags)'
                            }
                        >
                            {confirmDelete ? 'Click to confirm delete' : 'Delete category'}
                        </RoleButton>
                    ) : (
                        <span />
                    )}
                    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <RoleButton
                            scope="owner_only"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={submitting || !name.trim()}
                        >
                            {submitting
                                ? 'Saving…'
                                : mode === 'create'
                                  ? 'Create category'
                                  : 'Save changes'}
                        </RoleButton>
                    </div>
                </>
            }
        >
            <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-label" htmlFor="tax-cat-name">
                    Name
                </label>
                <input
                    id="tax-cat-name"
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Tempo, Energy, Use Case, Vocals"
                    maxLength={80}
                    autoFocus
                />
            </div>

            <div className="form-field" style={{ marginBottom: 16 }}>
                <span className="form-label">Colour</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {COLOR_PALETTE.map((c) => {
                        const selected = c.var === colorVar;
                        return (
                            <button
                                key={c.var}
                                type="button"
                                onClick={() => setColorVar(c.var)}
                                title={c.label}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 6,
                                    border: selected
                                        ? `2px solid ${c.hex}`
                                        : '1px solid var(--border)',
                                    background: `color-mix(in oklab, ${c.hex} 18%, transparent)`,
                                    color: c.hex,
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        background: c.hex,
                                        margin: '0 auto'
                                    }}
                                />
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-label" htmlFor="tax-cat-sort">
                    Sort order
                </label>
                <input
                    id="tax-cat-sort"
                    type="number"
                    className="form-input"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(Number(e.target.value) || 999)}
                    min={0}
                    style={{ width: 120 }}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                    Lower values appear first in pickers and the admin list.
                </span>
            </div>

            {error && (
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
            )}
        </Modal>
    );
}
